import { useState } from "react";
import { useNavigate } from "react-router-dom";
import QuotePreview from "./QuotePreview";
import ConfirmRedemptionModal from "./ConfirmRedemptionModal";

/**
 * Redemption form shell + AP-4F submit wiring.
 *
 * Submit is enabled when:
 *   - user is linked (session)
 *   - amount is non-empty
 *   - quote state is `active` (server confirmed the rate is available)
 *
 * On click → opens the confirmation modal which owns the actual POST.
 * On 202 success → navigate to /redeem/{arc_reference_id} for status
 * polling.
 *
 * `onSubmitted` lets the parent refresh its `Recent redemptions` list
 * before we navigate away.
 */
export default function RedemptionFormShell({ linked, me, onSubmitted }) {
  const [amount, setAmount] = useState("");
  const [quoteState, setQuoteState] = useState({ kind: "idle" });
  const [showConfirm, setShowConfirm] = useState(false);
  const navigate = useNavigate();

  const trimmed = amount.trim();
  const canSubmit = linked && trimmed !== "" && quoteState.kind === "active";

  let cta;
  if (!linked) {
    cta = "Link Assos365 to continue";
  } else if (trimmed === "") {
    cta = "Enter ARC amount";
  } else if (quoteState.kind === "unavailable") {
    cta = "Ecosystem rate temporarily unavailable";
  } else if (quoteState.kind === "invalid") {
    cta = "Fix the amount above";
  } else if (quoteState.kind === "error") {
    cta = "Quote service unreachable";
  } else if (quoteState.kind === "loading") {
    cta = "Calculating…";
  } else {
    cta = "Review & confirm";
  }

  const handleSuccess = (arcReferenceId) => {
    setShowConfirm(false);
    onSubmitted?.();
    navigate(`/redeem/${encodeURIComponent(arcReferenceId)}`);
  };

  return (
    <div data-testid="redeem-form-shell" className="glass rounded-md p-6">
      <h3 className="font-display text-sm uppercase tracking-[0.2em] text-white">
        Redeem ARC
      </h3>
      <p className="mt-1 font-mono text-[11px] text-white/50">
        Convert ARC into Assos365 credit at the current ecosystem rate.
      </p>

      <label
        htmlFor="redeem-arc-amount"
        className="mt-5 block font-mono text-[10px] uppercase tracking-[0.3em] text-white/50"
      >
        ARC amount
      </label>
      <div className="mt-2 flex items-center gap-3">
        <input
          id="redeem-arc-amount"
          data-testid="redeem-amount-input"
          inputMode="decimal"
          autoComplete="off"
          spellCheck={false}
          placeholder="e.g. 250"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
          className="flex-1 rounded-md border border-white/15 bg-black/60 px-3 py-2.5 font-mono text-base text-white outline-none focus:border-[#39FF14]/70 focus:ring-1 focus:ring-[#39FF14]/40"
        />
        <span className="font-display text-sm text-[#00F0FF]">ARC</span>
      </div>

      <div className="mt-4">
        <QuotePreview amount={amount} onStateChange={setQuoteState} />
      </div>

      <div className="mt-6 flex flex-col gap-2">
        <button
          type="button"
          disabled={!canSubmit}
          aria-disabled={!canSubmit}
          onClick={() => canSubmit && setShowConfirm(true)}
          data-testid="redeem-submit-btn"
          data-can-submit={canSubmit ? "true" : "false"}
          data-cta-copy={cta}
          title={canSubmit ? "Open the confirmation dialog" : "Submit is unavailable in this state"}
          className="btn-primary w-full text-xs disabled:cursor-not-allowed disabled:opacity-50"
        >
          {cta}
        </button>
        <p
          data-testid="redeem-submit-shell-note"
          className="font-mono text-[10px] text-center text-white/40"
        >
          Request-only redemption · Assos365 may review before credit is issued.
        </p>
      </div>

      {showConfirm && (
        <ConfirmRedemptionModal
          amount={trimmed}
          me={me}
          onClose={() => setShowConfirm(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}
